import React from "react";
import PropTypes from "prop-types";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";

import { useTopikMotion } from "../topikMotion";
import { commitHaptic } from "../haptics";
import { submitErrorKey } from "../submitError";
import ScrollCue from "../ScrollCue";

// Shared chrome for every analysis step: the dark topik-view frame, the (optional) back
// control (§6.3), the graceful game-end banner (§12.4, input left intact), the title /
// instruction, and the submit error. The step-specific inputs + submit render as children.
// `titleId` / `instructionId` let a step's text field take its name and description from them.
const AnalysisFrame = ({
  t,
  title,
  titleId,
  instruction = null,
  instructionId,
  heroIcon = null,
  canBack = false,
  submitting = false,
  gameClosed = false,
  error = null,
  onBack = () => {},
  children,
}) => {
  // Analysis steps settle on the calmer "thinking" clock (§1.2, A9).
  const { calm, step } = useTopikMotion();
  return (
    <>
      <Helmet>
        <meta name="theme-color" content="#222222" />
        <html className="card-background" />
      </Helmet>
      <motion.main
        className="topik-view"
        variants={step}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={calm}
      >
      {canBack && !submitting && (
        <div className="topik-analysis__back-row">
          <button
            type="button"
            className="topik-analysis__back"
            onClick={() => { commitHaptic(); onBack(); }}
            aria-label={t("topik-analysis.back")}
          >
            <ChevronLeftIcon fontSize="small" />
            <span>{t("topik-analysis.back")}</span>
          </button>
        </div>
      )}

      {gameClosed && (
        <div className="topik-view__closed-banner" role="status">
          {t("topik-play-view.closed.banner")}
        </div>
      )}

      {heroIcon && (
        <div className="topik-view__hero-icon" aria-hidden="true">{heroIcon}</div>
      )}
      <h1 id={titleId} className="topik-view__title">{title}</h1>
      {instruction && <p id={instructionId} className="topik-view__instruction">{instruction}</p>}

      {children}

      {error && (
        <p className="topik-view__error" role="alert">{t(submitErrorKey(error))}</p>
      )}
      <ScrollCue />
      </motion.main>
    </>
  );
};

AnalysisFrame.propTypes = {
  t: PropTypes.func.isRequired,
  title: PropTypes.node.isRequired,
  titleId: PropTypes.string,
  instruction: PropTypes.node,
  instructionId: PropTypes.string,
  heroIcon: PropTypes.node,
  canBack: PropTypes.bool,
  submitting: PropTypes.bool,
  gameClosed: PropTypes.bool,
  error: PropTypes.any,
  onBack: PropTypes.func,
  children: PropTypes.node.isRequired,
};

export default AnalysisFrame;
